import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Dialog } from '@material-ui/core';
import {
  CmpMemberInvoiceRowFragment, GetCmpMemberInvoicesDocument, GetCmpMemberInvoicesQuery,
  GetCmpMemberInvoiceDetailDocument, GetCmpMemberInvoiceDetailQuery,
  GetCmpMemberInvoiceDocumentDocument, GetCmpMemberInvoiceDocumentMutation,
  StartCmpMemberInvoiceCheckoutDocument, StartCmpMemberInvoiceCheckoutMutation,
  StartCmpMemberInvoiceCardFormDocument, StartCmpMemberInvoiceCardFormMutation,
  SaveCmpMemberInvoiceCardFormNoteDocument, SaveCmpMemberInvoiceCardFormNoteMutation,
  CmpInvoiceDisplayStateEnum,
} from '../../../../graphql/types';
import MemberCardPaymentForm from './MemberCardPaymentForm';
import { CmpSuccessDialog } from '../CmpProfile/CmpDesignDialog';
import useMemberDashboardAccess from '../../../common/Hooks/useMemberDashboardAccess';
import { memberInvoiceIdentityKey } from './memberInvoiceAccess';
import { getCreditedByRowLinkLines, getCreditNoteRowLinkLines } from './memberInvoiceCreditLinks';
import MemberAutoRenewal from './MemberAutoRenewal';
import { UserFacingError } from '../../../common/utilities/userFacingError';
import i18n from '../../../../locales/i18n';
import './memberInvoices.css';

type Row = CmpMemberInvoiceRowFragment;
type List = GetCmpMemberInvoicesQuery['getCmpMemberInvoices'];
type DocumentView = { url: string; filename: string };
const money = (amount: number, currency: string) => new Intl.NumberFormat('en-CA', {
  style: 'currency', currency,
}).format(amount / 100);
const labels: Record<string, string> = {
  AWAITING_PAYMENT: 'Awaiting Payment', OVERDUE: 'Overdue', PAID: 'Paid',
  CREDITED: 'Credited', REFUNDED: 'Refunded', VOIDED: 'Voided',
  WAIVED: 'Waived', NOT_PAID: 'Not Paid',
};
// A credit note names the invoice it credits and a source invoice names its
// credit notes (Oct 8 2026). Older rows without the links keep their note.
const statusNote = (row: Row): string => row.creditForInvoiceNumber
  ? 'Refunded'
  : row.installmentSummary || (row.daysPastDue ? `${row.daysPastDue} Days Overdue` : row.paymentMethodLabel !== 'Not paid' && row.paymentMethodLabel !== '-' ? row.paymentMethodLabel : '');
export const memberInvoiceCheckoutUrl = (value: string): string => {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com' || url.port || url.username || url.password) {
    throw new Error('The secure payment page link is not correct. Close this window and click Pay Now again.');
  }
  return url.href;
};
const operationId = () => Array.from(crypto.getRandomValues(new Uint8Array(16)))
  .map(value => value.toString(16).padStart(2, '0')).join('');
/** UUID v4 for the card form operation (the staff card form contract). */
export const cardFormOperationId = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Wave 2 (Oct 20 2026): texts when one invoice does not open. The old text blamed
 * an access change for every cause (network, PDF, no online payment).
 */
export const memberInvoiceOpenErrorText = (invoiceNumber: string, chamberName: string, cannotPayOnline: boolean) => {
  const values = { invoiceNumber, chamberName: chamberName.trim() || i18n.t('userFacingErrors.yourChamber') };
  return cannotPayOnline
    ? { list: i18n.t('userFacingErrors.invoiceCannotPayOnlineList', values), action: i18n.t('userFacingErrors.invoiceCannotPayOnline', values) }
    : { list: i18n.t('userFacingErrors.invoiceOpenFailedList', values), action: i18n.t('userFacingErrors.invoiceOpenFailed', values) };
};
const CANNOT_PAY_ONLINE = 'cannot-pay-online';

export type MemberInvoicesViewProps = {
  chamberName: string; timeZone?: string; data?: List; loading: boolean; error?: string;
  onRetry: () => void;
  onOpen: (row: Row, mode: 'document' | 'payment') => void;
  selected?: Row; mode?: 'document' | 'payment'; document?: DocumentView;
  busy: boolean; actionError?: string; onClose: () => void;
  onPay: () => void; onDocumentRetry: () => void; onPaymentOpen: () => void;
  /** Auto-renewal block under the table (design ACC:L1106-1110). */
  autoRenewal?: React.ReactNode;
  /** MD-I1: the Stripe card form in Process Payment; without it (event invoices) the secure Stripe page. */
  cardForm?: React.ReactNode;
  paidNotice?: string; onPaidNoticeClose?: () => void;
};

/** The real invoice surface. Data and actions are supplied by the authenticated container. */
export function MemberInvoicesView(props: MemberInvoicesViewProps) {
  const [zoom, setZoom] = useState('Fit on page');
  const pdfFrame = useRef<HTMLIFrameElement>(null);
  // MD-I11 (ACC:1599): Print PDF prints in place; the link opens the PDF only if the browser cannot print the frame.
  const printDocument = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const frame = pdfFrame.current?.contentWindow;
    if (!frame) return;
    try { frame.focus(); frame.print(); event.preventDefault(); } catch { /* the link opens the PDF instead */ }
  };
  useEffect(() => { setZoom('Fit on page'); }, [props.selected?.id]);
  // Moeen, Oct 20 2026: the design shows only a dash when an unpaid invoice
  // cannot be paid online. The dash keeps the design and gives the reason on
  // hover, tap or keyboard, because a hover title alone does not work on phones.
  const [reasonRowId, setReasonRowId] = useState<string>();
  const cannotPayText = (row: Row) => row.paymentUnavailableReason
    ? memberInvoiceOpenErrorText(row.invoiceNumber, props.chamberName, true).action : undefined;
  const outstanding = props.data?.outstanding || [];
  return <main className="member-invoices">
    <header className="mi-heading"><h1>Invoices &amp; Payments</h1>
      <p>View invoices issued by {props.chamberName} and pay any outstanding balance.</p>
</header>
    <div className="mi-card"><section className="mi-section" aria-label="Invoices">
      <h2>Invoices</h2>
      {props.loading ? <p role="status">Loading invoices…</p> : props.error ?
        <div role="alert"><p>{props.error}</p><button className="mi-button" onClick={props.onRetry}>Try again</button></div> : <>
        {outstanding.length ? <p className="mi-summary">You have <b>{outstanding.reduce((sum, item) => sum + item.count, 0)}</b> outstanding invoice(s) totalling <b>{outstanding.map(item => money(item.amountInCents, item.currency)).join(' + ')}</b>.</p> :
          <p className="mi-settled">All invoices are paid.</p>}
        {!!props.data?.rows.length && <div className="mi-table-scroll" tabIndex={0} aria-label="Invoice table, scroll horizontally for all columns">
          <table><thead><tr>{['Invoice #', 'Date', 'Description', 'Amount', 'Status', 'Action'].map(label => <th key={label}>{label}</th>)}</tr></thead>
            <tbody>{props.data.rows.map(row => <tr key={row.id}>
              <td><button className="mi-invoice-link" onClick={() => props.onOpen(row, 'document')}>{row.invoiceNumber}</button></td>
              <td>{new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: props.timeZone || 'UTC' }).format(row.issuedAtUtcMilli)}</td>
              <td>{row.description}</td><td className="mi-amount">{money(row.totalAmountInCents, row.currency)}</td>
              <td><strong className={`mi-status mi-status-${row.displayState.toLowerCase()}`}>{labels[row.displayState] || row.displayState.replace(/_/g, ' ')}</strong>
                <span className="mi-status-note">{statusNote(row)}</span>
                {[...getCreditNoteRowLinkLines(row.creditForInvoiceNumber), ...getCreditedByRowLinkLines(row.creditedByInvoiceNumbers)]
                  .map(line => <span key={line} className="mi-status-note mi-status-link" title={line}>{line}</span>)}</td>
              <td>{row.canPay ? <button className="mi-button mi-pay-button" onClick={() => props.onOpen(row, 'payment')}>Pay Now</button>
                : cannotPayText(row) ? <>
                  <button type="button" className="mi-no-action mi-no-action-reason" title={cannotPayText(row)} aria-label={cannotPayText(row)}
                    aria-expanded={reasonRowId === row.id} onClick={() => setReasonRowId(current => current === row.id ? undefined : row.id)}>—</button>
                  {reasonRowId === row.id && <span className="mi-status-note mi-no-action-note" role="note">{cannotPayText(row)}</span>}
                </> : <span className="mi-no-action">—</span>}</td>
            </tr>)}</tbody></table>
        </div>}
      </>}
      {props.autoRenewal}
    </section></div>
    <Dialog open={!!props.mode} fullWidth maxWidth={props.mode === 'document' ? false : 'sm'}
      fullScreen={props.mode === 'document'} aria-labelledby="mi-dialog-title" PaperProps={{ className: `mi-dialog mi-${props.mode}` }}>
      {props.mode === 'payment' ? <div className="mi-payment-content">
        <div className="mi-dialog-heading"><h2 id="mi-dialog-title">Process Payment</h2><button aria-label="Close payment" disabled={props.busy} onClick={props.onClose}>✕</button></div>
        <dl className="mi-payment-context"><div><dt>Bill To</dt><dd>{props.selected?.billToName}</dd></div>
          <div><dt>Reference Invoice</dt><dd>{props.selected?.invoiceNumber}</dd></div>
          <div><dt>Balance Owing</dt><dd>{props.selected && money(props.selected.amountDueInCents, props.selected.currency)}</dd></div></dl>
        {/* MD-I2 (ACC:1546-1548): design date and amount boxes; the server sets both, so they are read-only. */}
        <div className="mi-payment-fields">
          <label><span>PAYMENT DATE</span>
            <input readOnly value={new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: props.timeZone || 'UTC' }).format(Date.now())} /></label>
          <label><span>AMOUNT TO PAY</span>
            <span className="mi-amount-box"><span>$</span><input readOnly value={props.selected ? (props.selected.payableAmountInCents / 100).toFixed(2) : ''} /></span>
            {props.selected?.installmentSummary && <small>{props.selected.installmentSummary}</small>}</label>
        </div>
        {/* MD-I5 (ACC:1550-1557): design payment method box with the Credit Card tile. */}
        <div className="mi-method"><div className="mi-method-title">SELECT PAYMENT METHOD</div>
          <div className="mi-method-tile"><div>Credit Card</div><button type="button" aria-pressed="true" tabIndex={-1}>Select</button></div></div>
        {props.cardForm && !props.busy ? props.cardForm : <>
          <p>You will enter your card details on Stripe's secure payment page. This invoice updates after payment is confirmed.</p>
          {props.actionError && <p role="alert">{props.actionError}</p>}
          <div className="mi-payment-actions"><button className="mi-pay" disabled={props.busy || !props.selected?.canPay} onClick={props.onPay}>{props.busy ? 'Opening secure payment…' : 'Continue to Secure Payment'}</button>
            <button disabled={props.busy} onClick={props.onClose}>Cancel</button></div>
        </>}
      </div> : <>
        <div className="mi-document-toolbar"><h2 id="mi-dialog-title" className="mi-sr-only">Invoice {props.selected?.invoiceNumber}</h2>
          <div className="mi-zoom" aria-label="Document zoom">{['25%', '50%', '75%', '100%', 'Fit on page'].map(value => <button key={value} aria-pressed={zoom === value} onClick={() => setZoom(value)}>{value}</button>)}</div>
          <div className="mi-document-actions">{props.selected?.canPay && <button className="mi-pay" disabled={props.busy} onClick={props.onPaymentOpen}>Add Payment</button>}
            {props.document && <a className="mi-print" href={props.document.url} target="_blank" rel="noopener noreferrer" onClick={printDocument}><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="7"/></svg>Print PDF</a>}
            <button onClick={props.onClose} aria-label="Close document">✕</button></div>
        </div>
        {props.busy && <p role="status">Loading invoice document…</p>}
        {props.actionError && <div role="alert"><p>{props.actionError}</p><button onClick={props.onDocumentRetry}>Try again</button></div>}
        {props.document && <iframe ref={pdfFrame} key={`${props.document.url}-${zoom}`} title={`Invoice ${props.selected?.invoiceNumber} document`}
          className="mi-pdf" src={`${props.document.url}#toolbar=0&navpanes=0&${zoom === 'Fit on page' ? 'view=Fit' : `zoom=${zoom.replace('%', '')}`}`} />}
      </>}
    </Dialog>
    <CmpSuccessDialog open={!!props.paidNotice} onClose={() => props.onPaidNoticeClose?.()} message={props.paidNotice || ''} testId="member-payment-received" />
  </main>;
}

export default function MemberInvoices() {
  const client = useApolloClient();
  const { user, access, error: accessError, refetch } = useMemberDashboardAccess();
  const identity = user && access?.canViewInvoices && access.cmpEnabled
    ? memberInvoiceIdentityKey(user.id, user.chamberId, user.chamberMemberId || '', access.contactId) : '';
  const [data, setData] = useState<List>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<Row>();
  const [mode, setMode] = useState<'document' | 'payment'>();
  const [document, setDocument] = useState<DocumentView>();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const epoch = useRef(0);
  const paymentRequest = useRef<number>();
  const currentIdentity = useRef(identity); currentIdentity.current = identity;
  const operations = useRef(new Map<string, string>());
  const cardOperations = useRef(new Map<string, string>());
  // Event invoices (and any invoice the card form refuses) keep the secure Stripe page.
  const [hostedOnly, setHostedOnly] = useState(false);
  const [paidNotice, setPaidNotice] = useState<string>();
  const close = useCallback(() => {
    epoch.current += 1; paymentRequest.current = undefined; setMode(undefined); setSelected(undefined); setDocument(undefined); setBusy(false); setActionError(undefined);
    setHostedOnly(false);
  }, []);
  useEffect(() => () => { if (document) URL.revokeObjectURL(document.url); }, [document]);
  useEffect(() => {
    const request = ++epoch.current;
    setData(undefined); setError(undefined); setLoading(true); setMode(undefined); setSelected(undefined); setDocument(undefined); setBusy(false);
    if (!identity) { setLoading(false); return; }
    // MD-I23: the design list has no pages; load every invoice, 100 per request (the server limit).
    const loadAll = async () => {
      const page = (from: number) => client.query<GetCmpMemberInvoicesQuery>({ query: GetCmpMemberInvoicesDocument,
        variables: { input: { limit: 100, offset: from } }, fetchPolicy: 'no-cache' }).then(result => result.data.getCmpMemberInvoices);
      const first = await page(0);
      let rows = first.rows;
      while (rows.length < first.totalCount) {
        const next = await page(rows.length);
        if (!next.rows.length) break;
        rows = rows.concat(next.rows);
      }
      return { ...first, rows };
    };
    loadAll()
      .then(result => { if (request === epoch.current && currentIdentity.current === identity) setData(result); })
      .catch(() => { if (request === epoch.current) setError(i18n.t('userFacingErrors.invoiceListLoadFailed')); })
      .finally(() => { if (request === epoch.current) setLoading(false); });
    return () => { epoch.current += 1; };
  }, [client, identity, revision]);
  useEffect(() => { operations.current.clear(); paymentRequest.current = undefined; }, [identity]);
  // A window focus refresh reloads the list, which closes any open invoice. Clicking from
  // Stripe's card fields (an iframe) back to Confirm & Record Payment focuses the window,
  // so the refresh waits until no invoice is open.
  const invoiceOpen = useRef(false); invoiceOpen.current = !!selected;
  useEffect(() => {
    if (!identity) return;
    const refresh = () => { if (invoiceOpen.current) return; refetch().then(() => setRevision(value => value + 1)).catch(close); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refetch, close, identity]);
  // Design (Accelerator (3)): right after paying, the member sees the PAYMENT RECEIPT.
  const showingReceipt = useRef(false);
  const pendingReceipt = useRef<Row>();
  const open = async (row: Row, nextMode: 'document' | 'payment', paymentReceipt = false) => {
    const request = ++epoch.current;
    showingReceipt.current = paymentReceipt;
    setSelected(row); setMode(nextMode); setBusy(true); setActionError(undefined); setDocument(undefined);
    try {
      const detail = await client.query<GetCmpMemberInvoiceDetailQuery>({ query: GetCmpMemberInvoiceDetailDocument,
        variables: { input: { invoiceId: row.id } }, fetchPolicy: 'no-cache' });
      if (request !== epoch.current || currentIdentity.current !== identity) return;
      setSelected(detail.data.getCmpMemberInvoiceDetail);
      if (nextMode === 'document') {
        const response = await client.mutate<GetCmpMemberInvoiceDocumentMutation>({ mutation: GetCmpMemberInvoiceDocumentDocument,
          variables: { input: { invoiceId: row.id, paymentReceipt } }, fetchPolicy: 'no-cache' });
        if (request !== epoch.current || currentIdentity.current !== identity) return;
        const file = response.data?.getCmpMemberInvoiceDocument;
        if (!file || file.contentType !== 'application/pdf') throw new Error('The invoice PDF did not load. Click Try again.');
        const bytes = Uint8Array.from(atob(file.base64Data), char => char.charCodeAt(0));
        setDocument({ url: URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })), filename: file.filename });
      } else if (!detail.data.getCmpMemberInvoiceDetail.canPay) throw new UserFacingError(CANNOT_PAY_ONLINE);
    } catch (caught) {
      const text = memberInvoiceOpenErrorText(row.invoiceNumber, user?.chamberInfo.name || '',
        caught instanceof UserFacingError && caught.message === CANNOT_PAY_ONLINE);
      if (request === epoch.current) { setData(undefined); setError(text.list); setSelected(undefined); setActionError(text.action); }
    } finally { if (request === epoch.current) setBusy(false); }
  };
  const pay = async () => {
    if (!selected?.canPay || busy || paymentRequest.current !== undefined || !identity) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) { setActionError(i18n.t('userFacingErrors.paymentPageBlocked')); return; }
    popup.opener = null;
    const request = ++epoch.current; paymentRequest.current = request; setBusy(true); setActionError(undefined);
    try {
      const id = operations.current.get(selected.id) || operationId(); operations.current.set(selected.id, id);
      const response = await client.mutate<StartCmpMemberInvoiceCheckoutMutation>({ mutation: StartCmpMemberInvoiceCheckoutDocument,
        variables: { input: { invoiceId: selected.id, operationId: id } }, fetchPolicy: 'no-cache' });
      if (request !== epoch.current || currentIdentity.current !== identity) { popup.close(); return; }
      popup.location.href = memberInvoiceCheckoutUrl(response.data?.startCmpMemberInvoiceCheckout.checkoutUrl || '');
      close();
    } catch {
      popup.close();
      if (request === epoch.current) {
        const text = i18n.t('userFacingErrors.paymentPageNotOpened', { chamberName: user?.chamberInfo.name || i18n.t('userFacingErrors.yourChamber') });
        setData(undefined); setError(text); setSelected(undefined); setActionError(text);
      }
    } finally { if (paymentRequest.current === request) paymentRequest.current = undefined; if (request === epoch.current) setBusy(false); }
  };
  const startCardForm = async () => {
    const row = selected!;
    const id = cardOperations.current.get(row.id) || cardFormOperationId(); cardOperations.current.set(row.id, id);
    const response = await client.mutate<StartCmpMemberInvoiceCardFormMutation>({ mutation: StartCmpMemberInvoiceCardFormDocument,
      variables: { input: { invoiceId: row.id, operationId: id } }, fetchPolicy: 'no-cache' });
    const form = response.data?.startCmpMemberInvoiceCardForm;
    if (!form) throw new Error('The secure card form could not start. Close this window and click Pay Now again.');
    return form;
  };
  const saveCardNote = async (checkoutSessionId: string, note: string) => {
    await client.mutate<SaveCmpMemberInvoiceCardFormNoteMutation>({ mutation: SaveCmpMemberInvoiceCardFormNoteDocument,
      variables: { input: { invoiceId: selected!.id, checkoutSessionId, note } }, fetchPolicy: 'no-cache' });
  };
  // Stripe confirmed the card; the invoice shows Paid once Stripe tells CMP (webhook).
  const waitUntilPaid = async () => {
    const row = selected!;
    let paid = false;
    for (let attempt = 0; attempt < 20; attempt += 1) {
      await new Promise(resolve => window.setTimeout(resolve, 3000));
      try {
        const detail = await client.query<GetCmpMemberInvoiceDetailQuery>({ query: GetCmpMemberInvoiceDetailDocument,
          variables: { input: { invoiceId: row.id } }, fetchPolicy: 'no-cache' });
        if (detail.data.getCmpMemberInvoiceDetail.displayState === CmpInvoiceDisplayStateEnum.Paid) { paid = true; break; }
      } catch { /* keep waiting; the list refresh below shows the latest state */ }
    }
    cardOperations.current.delete(row.id);
    close();
    setRevision(value => value + 1);
    // The list reload closes any open invoice, so the receipt opens after it.
    if (paid) { pendingReceipt.current = row; return; }
    setPaidNotice(`Your payment for invoice ${row.invoiceNumber} was received.`);
  };
  useEffect(() => {
    const row = pendingReceipt.current;
    if (!row || (!data && !error)) return;
    pendingReceipt.current = undefined;
    if (data) void open(row, 'document', true);
    else setPaidNotice(`Your payment for invoice ${row.invoiceNumber} was received.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error]);
  if (!identity) return <main className="member-invoices"><div className="mi-card" role={accessError || access ? 'alert' : 'status'}>
    {accessError ? i18n.t('userFacingErrors.invoicesAccessCheckFailed')
      : access ? i18n.t('userFacingErrors.invoicesNoAccess', { chamberName: user?.chamberInfo.name || i18n.t('userFacingErrors.yourChamber') })
      : 'Checking invoice access…'}</div></main>;
  return <MemberInvoicesView chamberName={user?.chamberInfo.name || ''} timeZone={user?.chamberMemberProTimeZone || 'UTC'} data={data} loading={loading} error={error}
    onRetry={() => setRevision(value => value + 1)} onOpen={open} selected={selected} mode={mode}
    document={document} busy={busy} actionError={actionError} onClose={close} onPay={pay}
    onDocumentRetry={() => selected ? open(selected, 'document', showingReceipt.current) : close()}
    onPaymentOpen={() => selected && open(selected, 'payment')} autoRenewal={<MemberAutoRenewal />}
    cardForm={mode === 'payment' && selected?.canPay && !hostedOnly
      ? <MemberCardPaymentForm key={selected.id} start={startCardForm} saveNote={saveCardNote} chamberName={user?.chamberInfo.name}
        onPaid={() => { void waitUntilPaid(); }} onCancel={close} onUseHostedPage={() => setHostedOnly(true)} />
      : undefined}
    paidNotice={paidNotice} onPaidNoticeClose={() => setPaidNotice(undefined)} />;
}
